import { useNavigate } from "react-router-dom";
import { Button } from "../../../../components/buttons/button";
import { Icon } from "../../../../components/icon/icon";
import { Pill } from "../../../../components/pill";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { GetSingleUserResponse } from "../../../../redux/features/admin/users/users.types";
import { accountIcons, currencyShorts } from "../../../../utils/constants";
import { formatDate } from "../../../../utils/helpers";

export const SingleUserDetails = ({
  user,
  goToEvents = () => {},
  loading = false,
  isPayoutAccVerified = false,
  onDetailsEdit = () => {},
}: {
  user?: GetSingleUserResponse;
  goToEvents?: () => void;
  loading?: boolean;
  isPayoutAccVerified?: boolean;
  onDetailsEdit?: () => void;
}) => {
  const navigate = useNavigate();

  const socialLinks = [
    user?.data?.facebookLink,
    user?.data?.tiktokLink,
    user?.data?.instagramLink,
    user?.data?.twitterLink,
  ].filter(Boolean);
  const totalPastEvents =
    (user?.data.eventsCreated?.pastEvents?.organized ?? 0) +
    (user?.data.eventsCreated?.pastEvents?.attended ?? 0);
  const totalUpcomingEvents =
    (user?.data.eventsCreated?.upcomingEvents?.organized ?? 0) +
    (user?.data.eventsCreated?.upcomingEvents?.attended ?? 0);

  return (
    <>
      <BgWrapper
        loading={loading}
        loaderHeight={65}
        onClick={goToEvents}
        className="soft-shrink flex cursor-pointer items-center gap-4 text-grey-500"
      >
        <Icon svg="calendar" width={24} height={24} />

        <div className="flex flex-1 flex-col gap-[2px]">
          <p className="text-base font-bold text-grey-900">
            {(user?.data.userType === "guest" || totalUpcomingEvents === 0)
              ? `${totalPastEvents || "No"} Past Event`
              : `${totalUpcomingEvents || "No"} Upcoming Event`}
            {(totalPastEvents || totalUpcomingEvents) === 1 ? "" : "s"}
          </p>
          {user?.data.userType === "guest" ? (
            <p className="text-sm text-grey-600">
              Upcoming{" "}
              {totalUpcomingEvents} ∙
              Attended {user?.data.eventsCreated?.pastEvents?.attended ?? 0}
            </p>
          ) : (
            <p className="text-sm text-grey-600">
              Attended {user?.data.eventsCreated.pastEvents?.attended ?? 0} ∙
              Organized {user?.data.eventsCreated?.pastEvents?.organized ?? 0}
            </p>
          )}
        </div>

        <Icon svg="caret-down" width={24} height={24} className="-rotate-90" />
      </BgWrapper>

      <BgWrapper
        loading={loading}
        loaderHeight={65}
        className="flex items-center gap-4 text-grey-500"
      >
        <Icon svg="padlock" width={24} height={24} />

        <div className="flex flex-1 flex-col gap-[2px]">
          <p className="text-base font-bold text-grey-900">
            Signed up with {user?.data?.signupMethod}
          </p>
          <p className="text-sm text-grey-600">
            Joined on {formatDate(user?.data.dateJoined)}
          </p>
        </div>
      </BgWrapper>

      {user?.data?.organizerType === "Business" && (
        <BgWrapper
          loading={loading}
          loaderHeight={92}
          className="flex items-start gap-4 text-grey-500"
        >
          <img
            alt="organnization"
            src={user?.data?.organisation?.logo}
            className="!size-6 min-h-6 min-w-6 overflow-hidden rounded-full object-cover"
          />

          <div className="flex flex-1 flex-col gap-[2px]">
            <div className="flex items-center gap-3">
              <p className="text-base font-bold text-grey-900">
                {user?.data?.organisation?.name}
              </p>
              <Pill text={"BUSINESS"} variation={"grey"} />
            </div>

            <p className="text-sm text-grey-600">
              {user?.data?.organisation?.email}
            </p>
            <p className="text-sm text-grey-600">
              {user?.data?.organisation?.phoneNumber || "No phone number"}
            </p>
            <p className="text-sm text-blue-500">{user?.data?.website}</p>
          </div>

          <button
            type="button"
            className="w-fit"
            onClick={() => {
              onDetailsEdit();
            }}
          >
            <Icon svg="pencil" width={20} height={20} className="" />
          </button>
        </BgWrapper>
      )}

      <>
        {!(
          user?.data?.facebookLink ||
          user?.data?.instagramLink ||
          user?.data?.tiktokLink ||
          user?.data?.twitterLink
        ) ? (
          <BgWrapper>
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-4">
                <Icon
                  svg="link"
                  width={20}
                  height={20}
                  className="text-grey-600"
                />
                <div className="flex flex-col">
                  <p className="font-bold text-grey-900">No Social Links</p>
                  <p className="text-sm text-grey-600">
                    {user?.data.firstName} has not added any links
                  </p>
                </div>
              </div>
              <Icon
                svg="pencil"
                width={20}
                height={20}
                className="text-grey-500"
              />
            </div>
          </BgWrapper>
        ) : (
          <BgWrapper>
            <div className="flex w-full items-center justify-between">
              <div className="flex items-center gap-4">
                <Icon
                  svg="link"
                  width={20}
                  height={20}
                  className="text-grey-600"
                />
                <div className="flex flex-col">
                  <p className="text-base font-bold text-grey-900">
                    {socialLinks.length} social link
                    {socialLinks.length !== 1 && "s"}
                  </p>
                  <div className="flex flex-wrap items-center gap-2.5">
                    <div className="w-[102px]">
                      {user?.data?.facebookLink && (
                        <Button
                          text={"Facebook"}
                          type="button"
                          icon="facebook-fill"
                          size="smaller"
                          iconDimension={16}
                          className="text-sm font-semibold text-grey-600"
                          iconClassName="mb-1"
                          variation="social-plain-grey"
                        />
                      )}
                    </div>
                    <div className="w-[77px]">
                      {user?.data?.tiktokLink && (
                        <Button
                          text={"Tiktok"}
                          type="button"
                          icon="tiktok"
                          size="smaller"
                          iconDimension={16}
                          className="text-sm font-semibold text-grey-600"
                          iconClassName="mb-1"
                          variation="social-plain-grey"
                        />
                      )}
                    </div>
                    <div className="w-[102px]">
                      {user?.data?.instagramLink && (
                        <Button
                          text={"Instagram"}
                          type="button"
                          icon="instagram"
                          size="smaller"
                          iconDimension={16}
                          className="text-sm font-semibold text-grey-600"
                          variation="social-plain-grey"
                        />
                      )}
                    </div>
                    <div className="w-[84px]">
                      {user?.data?.twitterLink && (
                        <Button
                          text={"Twitter"}
                          type="button"
                          icon="twitter"
                          size="smaller"
                          iconDimension={16}
                          className="text-sm font-semibold text-grey-600"
                          iconClassName="mb-1"
                          variation="social-plain-grey"
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <Icon
                svg="pencil"
                width={20}
                height={20}
                className="text-grey-500"
              />
            </div>
          </BgWrapper>
        )}
      </>

      {user?.data?.agent && Object.keys(user.data.agent).length > 0 ? (
        <BgWrapper
          loading={loading}
          loaderHeight={65}
          className="soft-shrink flex cursor-pointer items-center gap-4 text-grey-500"
          onClick={() => {
            navigate(`/sales/agent/${user.data.agent?._id}`);
          }}
        >
          <Icon svg="megaphone" width={24} height={24} />

          <div className="flex flex-1 flex-col gap-[2px]">
            <p className="text-base font-bold text-grey-900">
              Invited by Sales Agent
            </p>
            <p className="text-sm text-grey-600">
              Linked to {user.data.agent.firstName} {user.data.agent.lastName}
            </p>
          </div>

          <Icon
            svg="caret-down"
            width={24}
            height={24}
            className="-rotate-90"
          />
        </BgWrapper>
      ) : undefined}

      {user?.data?.userType === "organiser" ? (
        <>
          <BgWrapper>
            <p className="text-base font-bold text-grey-950">
              Payout Accounts{" "}
            </p>
          </BgWrapper>

          {(user?.data?.payoutAccount?.length ?? 0) > 0 ? (
            <>
              {user?.data?.payoutAccount?.map((payoutAccount, idx) => {
                return (
                  <BgWrapper
                    key={`${idx}-payout-${payoutAccount.currency}`}
                    loading={loading}
                    loaderHeight={65}
                    className="flex items-center gap-4 text-grey-500"
                  >
                    <Icon
                      svg={accountIcons[payoutAccount?.currency ?? "NGN"]}
                      width={24}
                      height={24}
                    />

                    <div className="flex-1">
                      <MiniHeader
                        header={
                          <div className="flex w-fit items-center gap-1">
                            {payoutAccount?.currency === "Canadian Dollars"
                              ? payoutAccount?.interacName
                              : payoutAccount?.accountName}
                            <Pill
                              text={currencyShorts[payoutAccount?.currency]}
                              variation="grey"
                              className="!uppercase"
                            />
                          </div>
                        }
                        subheader={`${payoutAccount?.currency === "Canadian Dollars" ? payoutAccount?.interacEmail : payoutAccount?.accountNumber} ∙ ${payoutAccount?.currency === "Canadian Dollars" ? "Interac" : payoutAccount?.bankName}`}
                      />
                    </div>

                    {!isPayoutAccVerified &&
                    payoutAccount?.currency === "NGN" ? (
                      <Pill text="UNVERIFIED" variation="grey" />
                    ) : undefined}
                  </BgWrapper>
                );
              })}
            </>
          ) : (
            <>
              <BgWrapper
                loading={loading}
                loaderHeight={65}
                className="flex items-center gap-4 text-grey-500"
              >
                <Icon svg="bank" width={24} height={24} />

                <div className="flex flex-1 flex-col gap-[2px]">
                  <p className="text-base font-bold text-grey-900">
                    No payout accounts
                  </p>
                  <p className="text-sm text-grey-600">
                    {user?.data?.firstName} has a draft event and has not added
                    a payout account yet.
                  </p>
                </div>
              </BgWrapper>
            </>
          )}
        </>
      ) : undefined}
    </>
  );
};
